/**
 * Realtime client: ONE multiplexed stream per tab (P2 §3.6).
 *
 * Ported from India dashboard/src/realtime.js (211 lines): the `hello` handshake, the 45 s stale detector and the
 * subscriber registry are kept.
 * India hooks removed: the India leads-stream URL with session_id+username in the query, the per-lead messaging-app delivery status synthesis, the separate notification/booking streams. US changes: cookie auth, `/api/v1/stream`, typed envelopes
 * `{id,type,entity,entity_id,version,data,at}`, Last-Event-ID resume, exponential backoff, `reset` → refetch-all.
 *
 * In fixture mode the same registry is fed by the in-memory fixture bus instead of an EventSource.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { EVENT_NAMES } from '@shared/events.mjs';
import { API_BASE } from './http';
import type { StreamEvent } from './types';

type Handler = (e: StreamEvent) => void;
export type StreamStatus = 'connecting' | 'live' | 'paused' | 'off';

const handlers = new Map<string, Set<Handler>>(); // type → handlers ('*' = all)
const statusSubs = new Set<() => void>();
let status: StreamStatus = 'off';
let lastMessageTs = 0;
let lastEventId: string | null = null;
let es: EventSource | null = null;
let staleTimer: number | null = null;
let retryTimer: number | null = null;
let attempt = 0;
let downSince: number | null = null;
let onReset: (() => void) | null = null;
let fixtureOff: (() => void) | null = null;
/** P1 keeps the pipe alive with SSE comments (`: ka`), which EventSource never surfaces; the stale check only arms once
 * the server sends observable `ping` events, otherwise a quiet tenant would reconnect every 45 s. */
let serverPings = false;

let degraded = false;
let degradeTimer: number | null = null;
const notify = () => statusSubs.forEach((f) => f());
function setStatus(s: StreamStatus) {
  if (s === status) return;
  status = s;
  if (s === 'live' || s === 'off') {
    downSince = null; degraded = false;
    if (degradeTimer != null) { clearTimeout(degradeTimer); degradeTimer = null; }
  } else if (downSince == null) {
    downSince = Date.now();
    degradeTimer = window.setTimeout(() => { degraded = true; notify(); }, 60_000);
  }
  notify();
}

/** Dispatch one envelope to its type handlers and to '*' handlers. Handlers must be idempotent (replays happen). */
export function dispatch(e: StreamEvent) {
  lastMessageTs = Date.now();
  if (e.id) lastEventId = e.id;
  for (const key of [e.type, '*']) {
    const set = handlers.get(key);
    if (set) for (const h of set) { try { h(e); } catch (err) { console.error('[stream] handler', e.type, err); } }
  }
}

/**
 * P1 frame (core/events-hub.js): `id: <outbox id>`, `event: <event_type>`,
 * `data: {id, event_type, entity, entity_id, payload, created_at, replay?}`. Ephemeral events (tech.location) have no id.
 * The older `{type, data, at}` envelope is still accepted (fixtures).
 */
function parse(ev: MessageEvent, type?: string): StreamEvent | null {
  try {
    const j = JSON.parse(ev.data);
    const data = j.payload ?? j.data ?? {};
    return {
      id: ev.lastEventId || (j.id != null ? String(j.id) : ''), type: j.event_type ?? j.type ?? type ?? 'message',
      entity: j.entity ?? '', entity_id: j.entity_id ?? '', version: j.version ?? data.version ?? null, data,
      at: j.created_at ?? j.at ?? new Date().toISOString(),
    };
  } catch { return null; }
}

function open() {
  if (es && es.readyState !== EventSource.CLOSED) return;
  setStatus('connecting');
  // EventSource re-sends Last-Event-ID itself on its own reconnects; after a manual reopen we pass it as a query param.
  const url = `${API_BASE}/v1/stream${lastEventId ? `?last_event_id=${encodeURIComponent(lastEventId)}` : ''}`;
  es = new EventSource(url, { withCredentials: true });
  es.onopen = () => { attempt = 0; lastMessageTs = Date.now(); setStatus('live'); };
  es.onmessage = (ev) => { const e = parse(ev); if (e) dispatch(e); };
  es.addEventListener('hello', () => { lastMessageTs = Date.now(); setStatus('live'); });
  es.addEventListener('ping', () => { lastMessageTs = Date.now(); serverPings = true; });
  // Replay gap (`resync`, P1; `reset` in older drafts) → refetch everything.
  for (const name of ['reset', 'resync']) es.addEventListener(name, () => { lastMessageTs = Date.now(); onReset?.(); });
  // Session revoked / user disabled: stop reconnecting; the next API call's 401 routes to /login.
  es.addEventListener('logout', () => { es?.close(); es = null; setStatus('paused'); });
  for (const name of EVENT_NAMES) es.addEventListener(name, (ev) => { const e = parse(ev as MessageEvent, name); if (e) dispatch(e); });
  es.onerror = () => {
    if (!es) return;
    if (es.readyState === EventSource.CLOSED) {
      // Server closed us (401, deploy, proxy timeout): back off and reopen ourselves.
      es = null;
      setStatus('paused');
      const delay = Math.min(30_000, 1000 * 2 ** attempt++) + Math.random() * 500;
      retryTimer = window.setTimeout(open, delay);
    } else setStatus('connecting');
  };
}

/** Open the live stream (idempotent). `reset` fires when the server reports a replay gap → refetch everything. */
export function connect(opts: { onReset: () => void }) {
  onReset = opts.onReset;
  open();
  if (staleTimer == null) {
    // India's 45 s stale check; our server heartbeats every 25 s, so 45 s of silence means the pipe is dead.
    staleTimer = window.setInterval(() => {
      if (!es || !serverPings) return;
      if (Date.now() - lastMessageTs > 45_000) { es.close(); es = null; setStatus('paused'); open(); }
    }, 10_000);
  }
}

/** Fixture mode: the in-memory bus feeds the same registry. */
export function connectFixture(subscribe: (emit: (e: StreamEvent) => void) => () => void) {
  fixtureOff?.();
  fixtureOff = subscribe(dispatch);
  setStatus('live');
}

export function close() {
  es?.close(); es = null;
  fixtureOff?.(); fixtureOff = null;
  if (staleTimer != null) { clearInterval(staleTimer); staleTimer = null; }
  if (retryTimer != null) { clearTimeout(retryTimer); retryTimer = null; }
  lastEventId = null;
  setStatus('off');
}

/** Subscribe to an event type ('*' for all). Returns the unsubscribe function. */
export function on(type: string, h: Handler) {
  let set = handlers.get(type);
  if (!set) handlers.set(type, (set = new Set()));
  set.add(h);
  return () => { set!.delete(h); if (!set!.size) handlers.delete(type); };
}

/** React: subscribe for the component's lifetime. The latest handler is always called (no stale closures). */
export function useStreamEvent(type: string | string[], h: Handler) {
  const ref = useRef(h);
  ref.current = h;
  const key = Array.isArray(type) ? type.join('|') : type;
  useEffect(() => {
    const offs = key.split('|').map((t) => on(t, (e) => ref.current(e)));
    return () => offs.forEach((f) => f());
  }, [key]);
}

/** React: live / paused status, plus whether we've been down long enough (>60 s) to fall back to polling. */
export function useStreamStatus() {
  const snap = useSyncExternalStore((f) => { statusSubs.add(f); return () => { statusSubs.delete(f); }; }, () => `${status}|${degraded ? 1 : 0}`);
  const [s, dg] = snap.split('|');
  return { status: s as StreamStatus, degraded: dg === '1' };
}
/** For TanStack `refetchInterval`: poll every 30 s only while the stream has been down > 60 s (P2 §3.6). */
export const pollWhenDegraded = () => (degraded ? 30_000 : false);
